/* eslint-disable camelcase */
import { ReactNode } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Paper, Typography } from '@mui/material';
import { ExpandMore, NotificationsActiveOutlined, ReceiptLongOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import { labelOf } from 'src/modules/sending/components/deliveryMethodPicker';
import { toCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';
import { SendingOrderStatusBadge } from 'src/modules/sending/components/sendingOrderStatusBadge';
import { SendingOrderStatusEnum } from 'src/modules/sending/models/sendingOrderStatus';

interface SendingOrderReminderLandingProps {
  order: SendingOrder;
  // Påminnelser kan beställas först när huvudutskicket är inskickat (fakturamallen skapas då).
  canOrderReminders: boolean;
  hasDraft: (which: 1 | 2) => boolean;
  onSelect: (which: 1 | 2) => void;
  // Öppnar huvudutskickets steg – för att komplettera en beställning som skickats in för komplettering, eller
  // för att läsa igenom en inskickad beställning steg för steg.
  onOpenMain: () => void;
  // Huvudutskickets sammanfattning, visas när raden fälls ut.
  summary: ReactNode;
}

const fmt = (value: string | Date | undefined): string => (value ? dayjs(value).format('D MMM YYYY') : '–');

// Utskicksdatum visas alltid som kundens datum (DB-datum − 1 dag), samma konvention som headern och datumkorten
// i guiden. Förfallodatum är riktiga datum och räknas inte om.
const fmtSend = (value: string | Date | undefined): string => {
  const d = toCustomerSendDate(value);
  return d ? d.format('D MMM YYYY') : '–';
};

const methodLabels = (csv: string | undefined): string =>
  (csv ?? '')
    .split(',')
    .filter((k) => k.length > 0)
    .map((k) => labelOf(k))
    .join(', ') || '–';

// Utskicksdatumet som ett klassiskt kalenderblad: dag, månad, år. Ger raden en tydlig ankarpunkt i tid.
const DateCard = ({ value }: { value: string | Date | undefined }) => {
  // Kundens utskicksdatum, inte DB-datumet – annars skiljer kortet en dag från rubriken.
  const date = toCustomerSendDate(value);

  return (
    <Box
      sx={{
        width: 56,
        flex: 'none',
        textAlign: 'center',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant='caption'
        sx={{ display: 'block', bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700, letterSpacing: 0.5, py: 0.15 }}
      >
        {date ? date.format('MMM').toUpperCase() : '—'}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.15, pt: 0.25 }}>{date ? date.format('D') : '–'}</Typography>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', pb: 0.4 }}>
        {date ? date.format('YYYY') : ''}
      </Typography>
    </Box>
  );
};

// En etikett/värde-rad i ett utskicks sammanfattning.
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant='body2' color='text.secondary' sx={{ minWidth: 180 }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Landningsvyn för en inskickad beställning. Varje beställt utskick – huvudutskicket och de påminnelser som
 * redan är beställda – ligger som en utfällbar rad, i den ordning de skickas. Under dem står det som återstår:
 * frågan om att beställa fler påminnelser. Stegen visas först när ett utskick är valt.
 */
export const SendingOrderReminderLanding = ({
  order,
  canOrderReminders,
  hasDraft,
  onSelect,
  onOpenMain,
  summary,
}: SendingOrderReminderLandingProps) => {
  const reminderOrdered = (which: 1 | 2) => (which === 1 ? !!order.reminder1_date_id : !!order.reminder2_date_id);

  // Rad för ett beställt utskick: hopfälld visar status och datum, utfälld visar detaljerna.
  const renderOrderedRow = (options: {
    title: string;
    icon: ReactNode;
    status: number | undefined;
    sendDate: string | Date | undefined;
    payDate: string | Date | undefined;
    details: ReactNode;
  }) => (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        '&:before': { display: 'none' },
        '&:hover': { borderColor: 'grey.400' },
        '&.Mui-expanded': { borderColor: 'primary.main' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <DateCard value={options.sendDate} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {options.icon}
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                {options.title}
              </Typography>
              <SendingOrderStatusBadge status={options.status} />
            </Box>
            {/* Datumet gäller fakturorna, inte beställningen – därför den längre etiketten. */}
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              Fakturornas förfallodatum {fmt(options.payDate)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{options.details}</AccordionDetails>
    </Accordion>
  );

  const renderOrderedReminder = (which: 1 | 2) => {
    const status = which === 1 ? order.reminder1_status : order.reminder2_status;
    const date = which === 1 ? order.reminder1_date?.sndDate : order.reminder2_date?.sndDate;
    const payDate = which === 1 ? order.reminder1_payDate : order.reminder2_payDate;
    const methods = which === 1 ? order.reminder1_deliveryMethodOrder : order.reminder2_deliveryMethodOrder;
    const submitted = which === 1 ? order.reminder1_submitted : order.reminder2_submitted;
    // Bokad men väntar på texter – då är knappen "Komplettera texterna", inte bara "Öppna".
    const awaitingCompletion = status === SendingOrderStatusEnum.KompletteringBehovs;

    return renderOrderedRow({
      title: `Påminnelse ${which}`,
      icon: <NotificationsActiveOutlined color='action' />,
      status: status ?? SendingOrderStatusEnum.Inskickad,
      sendDate: date,
      payDate,
      details: (
        <Box>
          <DetailRow label='Utskicksdatum' value={fmtSend(date)} />
          <DetailRow label='Fakturornas förfallodatum' value={fmt(payDate)} />
          <DetailRow label='Leveransmetoder' value={methodLabels(methods)} />
          <DetailRow label='Beställd' value={fmt(submitted)} />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            {awaitingCompletion
              ? 'Utskicket är bokat, men texterna är inte ifyllda ännu. Komplettera dem så skickas påminnelsen in.'
              : 'Påminnelsen går till dem som inte har betalat sin faktura – antalet mottagare fastställs när den produceras.'}
          </Typography>
          <Button
            variant={awaitingCompletion ? 'contained' : 'outlined'}
            disableElevation
            size='small'
            sx={{ mt: 2 }}
            onClick={() => onSelect(which)}
          >
            {awaitingCompletion ? 'Komplettera texterna' : 'Öppna påminnelsen'}
          </Button>
        </Box>
      ),
    });
  };

  const renderReminderCard = (which: 1 | 2) => {
    const blockedByFirst = which === 2 && !reminderOrdered(1);
    const disabled = !canOrderReminders || blockedByFirst;

    const helper = (() => {
      if (!canOrderReminders) return 'Kan beställas när huvudutskicket är inskickat';
      if (blockedByFirst) return 'Beställ påminnelse 1 först';
      return hasDraft(which) ? 'Påbörjad – inte inskickad' : 'Skicka en påminnelse till dem som inte betalat';
    })();

    return (
      <Paper
        key={which}
        variant='outlined'
        sx={{ flex: 1, minWidth: 260, p: 3, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start', opacity: disabled ? 0.6 : 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveOutlined color={disabled ? 'disabled' : 'primary'} />
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Påminnelse {which}
          </Typography>
        </Box>

        <Typography variant='body2' color='text.secondary' sx={{ minHeight: 40 }}>
          {helper}
        </Typography>

        <Button variant='contained' disableElevation disabled={disabled} onClick={() => onSelect(which)}>
          {hasDraft(which) ? 'Fortsätt' : `Beställ påminnelse ${which}`}
        </Button>
      </Paper>
    );
  };

  const notOrdered = ([1, 2] as (1 | 2)[]).filter((which) => !reminderOrdered(which));
  // Huvudutskicket väntar på komplettering – då är det det som ska göras först, inte påminnelserna.
  const mainNeedsCompletion = order.status === SendingOrderStatusEnum.KompletteringBehovs;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      {/* Beställda utskick i den ordning de skickas – huvudutskicket först, sedan påminnelserna. */}
      {renderOrderedRow({
        title: 'Huvudutskick',
        icon: <ReceiptLongOutlined color='action' />,
        status: order.status,
        sendDate: order.date?.sndDate,
        payDate: order.payDate,
        details: (
          <Box>
            {mainNeedsCompletion && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                Beställningen är inskickad men behöver kompletteras innan den kan produceras – texterna är inte klara.
              </Alert>
            )}
            {summary}
            <Button variant={mainNeedsCompletion ? 'contained' : 'outlined'} disableElevation sx={{ mt: 2 }} onClick={onOpenMain}>
              {mainNeedsCompletion ? 'Komplettera beställningen' : 'Öppna beställningen'}
            </Button>
          </Box>
        ),
      })}
      {reminderOrdered(1) && renderOrderedReminder(1)}
      {reminderOrdered(2) && renderOrderedReminder(2)}

      {notOrdered.length > 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
            Vill du beställa {notOrdered.length === 2 ? 'påminnelser' : `påminnelse ${notOrdered[0]}`}?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            En påminnelse går till dem som inte har betalat. Den har egna datum, leveransmetoder och texter – men samma urval som huvudutskicket.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>{notOrdered.map((which) => renderReminderCard(which))}</Box>
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 3 }}>
          Både påminnelse 1 och 2 är beställda. Föreningssupport hör av sig om något behöver kompletteras.
        </Typography>
      )}
    </Box>
  );
};
